import { CourseMaterialTypeConstants } from "../constants/CourseMaterialTypeConstansts";
import { CourseStatusConstants } from "../constants/CourseStatusConstants";
import { CourseTypeConstants } from "../constants/CourseTypeConstants";
import { EvaluationStatusConstants } from "../constants/EvaluationStatusConstants";
import { ExternalLinkConstants } from "../constants/ExternalLinkConstants";
import { CourseDTO } from "../services/apiClient";
import { convertToUserTime } from "./dateUtils/convertToUserTime";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const hasMaterialDownloaded = (course: CourseDTO): boolean => {
    return !!course.timeMaterialDownloaded;
};

const hasMaterialShipped = (course: CourseDTO): boolean => {
    return !!course.timeMaterialShipped;
};

const hasMaterialReceived = (course: CourseDTO): boolean => {
    return hasMaterialDownloaded(course) || hasMaterialShipped(course);
};

const isDownloadMaterialType = (course: CourseDTO): boolean => {
    return course.courseMaterialTypeId === CourseMaterialTypeConstants.DOWNLOAD;
};

const isPrintMaterialType = (course: CourseDTO): boolean => {
    return course.courseMaterialTypeId === CourseMaterialTypeConstants.PRINT;
};

const getMaterialReceivedDate = (course: CourseDTO): Date | null => {
    if (hasMaterialDownloaded(course)) {
        return new Date(course.timeMaterialDownloaded!);
    }
    if (hasMaterialShipped(course)) {
        return new Date(course.timeMaterialShipped!);
    }
    return null;
};

const getEvaluationAvailableDate = (course: CourseDTO): Date => {
    const materialDate = getMaterialReceivedDate(course);
    if (!materialDate) throw new Error('Material date not available');

    const evaluationDate = new Date(materialDate);
    evaluationDate.setDate(evaluationDate.getDate() + 2);
    return evaluationDate;
};

export const getCourseFee = (course: CourseDTO): number => {
    var dedicatedStatusFee = course.courseType?.dedicatedStatusFee || 0;
    var materialFee = course.courseMaterialType?.fee || 0;

    return dedicatedStatusFee + materialFee;
};

// ============================================================================
// STUDY MATERIAL FUNCTIONS
// ============================================================================

export const shouldShowStudyMaterial = (course: CourseDTO): boolean => {
    return (isDownloadMaterialType(course) || isPrintMaterialType(course)) &&
        course.courseStatusId === CourseStatusConstants.PAYMENT_RECEIVED ||
        course.courseStatusId === CourseStatusConstants.SHIPPED ||
        course.courseStatusId === CourseStatusConstants.EVALUATION_FAILED;
};

export const shouldShowStudyMaterialButton = (course: CourseDTO): boolean => {
    return isDownloadMaterialType(course) &&
        course.courseStatusId === CourseStatusConstants.PAYMENT_RECEIVED ||
        course.courseStatusId === CourseStatusConstants.EVALUATION_FAILED;
};

export const getStudyMaterialMessage = (course: CourseDTO): string => {
    if (isPrintMaterialType(course)) {
        if (hasMaterialShipped(course) && course.courseStatusId === CourseStatusConstants.SHIPPED || course.courseStatusId === CourseStatusConstants.EVALUATION_FAILED) {
            const shippedDate = convertToUserTime(String(course.timeMaterialShipped), true);
            return `Sent via courier to address: ${course.courierAddress || 'N/A'} on ${shippedDate}.
                    Your waybill number is: ${course.courierTrackingReference || 'N/A'}, you can track your package below.`;
        }

        return 'Busy processing package for delivery.';
    }
    return 'Download and review the Study Material manual.';
};

export const shouldShowPrintProcessingMessage = (course: CourseDTO): boolean => {
    const test = isPrintMaterialType(course) &&
        course.courseStatusId === CourseStatusConstants.PAYMENT_RECEIVED &&
        !hasMaterialShipped(course);

    return test;
};

export const shouldShowTrackingButton = (course: CourseDTO): boolean => {
    return isPrintMaterialType(course) && hasMaterialShipped(course) && (course.courseStatusId === CourseStatusConstants.SHIPPED || course.courseStatusId === CourseStatusConstants.EVALUATION_FAILED);
};

// ============================================================================
// EVALUATION TIMING FUNCTIONS
// ============================================================================

export const hasPassedEvaluationWaitPeriod = (course: CourseDTO): boolean => {
    if (!hasMaterialReceived(course)) return false;

    // Check if 2+ days have passed since material download/shipment
    const materialDate = getMaterialReceivedDate(course);
    if (!materialDate) return false;

    const twoDaysAgo = new Date();
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

    return materialDate <= twoDaysAgo;
};

export const shouldShowEvaluationWaitField = (course: CourseDTO): boolean => {
    // Show the wait field only when material is received but wait period hasn't passed
    return hasMaterialReceived(course) && !hasPassedEvaluationWaitPeriod(course);
};

export const shouldShowEvaluationStatusField = (course: CourseDTO): boolean => {
    // Show the status field only when wait period has passed
    return hasMaterialReceived(course) && hasPassedEvaluationWaitPeriod(course);
};

export const shouldShowEvaluationButton = (course: CourseDTO): boolean => {
    return hasPassedEvaluationWaitPeriod(course) && (
        course.courseStatusId === CourseStatusConstants.PAYMENT_RECEIVED ||
        course.courseStatusId === CourseStatusConstants.SHIPPED ||
        course.courseStatusId === CourseStatusConstants.EVALUATION_FAILED
    );
};

// ============================================================================
// MESSAGE GENERATION FUNCTIONS
// ============================================================================

export const getEvaluationWaitMessage = (course: CourseDTO): string => {
    if (!hasMaterialReceived(course)) return '';

    const evaluationDate = getEvaluationAvailableDate(course);
    const materialType = isDownloadMaterialType(course) ? 'downloading/receiving' : 'receiving';

    return `Please be aware that you can only start your evaluation after 2 days of ${materialType} 
    your study material, this will give you enough time to study. 
    You may attempt your evaluation on ${convertToUserTime(evaluationDate.toISOString(), false)}.`;
};

export const getEvaluationMessage = (course: CourseDTO): string => {
    return !course.evaluationStatusId
        ? 'Complete evaluation and score 70% or more.'
        : `You have ${course.evaluationStatusId === EvaluationStatusConstants.PASSED.toLocaleLowerCase() ? 'passed' : 'failed'} the evaluation with a score of ${course.evaluationScorePercentage}%.`;
};

// ============================================================================
// SCORE STATUS FUNCTIONS
// ============================================================================

export const shouldShowScoreStatus = (course: CourseDTO): boolean => {
    return course.courseStatusId === CourseStatusConstants.STATUS_PENDING ||
        course.courseStatusId === CourseStatusConstants.STATUS_APPROVED;
};

export const getScoreStatusMessage = (course: CourseDTO): string => {
    if (course.courseStatusId === CourseStatusConstants.STATUS_PENDING ||
        course.courseStatusId === CourseStatusConstants.STATUS_APPROVED) {
        return 'Practical Approved';
    }
    return '';
};

export const shouldShowPracticalTarget = (course: CourseDTO): boolean => {
    // Hide if not download/print material type
    if (course.courseMaterialTypeId !== CourseMaterialTypeConstants.DOWNLOAD &&
        course.courseMaterialTypeId !== CourseMaterialTypeConstants.PRINT) {
        return false;
    }

    // Hide if status is pending, approved, or forfeited
    return !(course.courseStatusId === CourseStatusConstants.PENDING ||
        course.courseStatusId === CourseStatusConstants.STATUS_PENDING ||
        course.courseStatusId === CourseStatusConstants.STATUS_APPROVED ||
        course.courseStatusId === CourseStatusConstants.FORFEITED);
};

// ============================================================================
// TARGET/PRACTICAL FUNCTIONS
// ============================================================================

export const getPracticalTargetMessage = (course: CourseDTO): string => {
    if (course.courseStatusId === CourseStatusConstants.EVALUATION_PASSED) {
        return 'Complete the practical evaluation by submitting one (1) target.';
    }
    return 'Download the target and upload your score after completing the evaluation.';
};

export const shouldShowDownloadTargetButton = (course: CourseDTO): boolean => {
    if (
        course.courseStatusId === CourseStatusConstants.PENDING ||
        course.courseStatusId === CourseStatusConstants.SCORES_APPROVED ||
        course.courseStatusId === CourseStatusConstants.STATUS_PENDING ||
        course.courseStatusId === CourseStatusConstants.STATUS_APPROVED ||
        course.courseStatusId === CourseStatusConstants.FORFEITED) return false;
    return true;
};

export const shouldShowSubmitTargetButton = (course: CourseDTO): boolean => {
    return course.courseStatusId === CourseStatusConstants.EVALUATION_PASSED ||
        course.courseStatusId === CourseStatusConstants.SCORES_SUBMITTED ||
        course.courseStatusId === CourseStatusConstants.SCORES_DECLINED;
};

export const getTargetLink = (course: CourseDTO): string => {
    switch (course.courseTypeId) {
        case CourseTypeConstants.DEDICATED_HUNTER:
            return ExternalLinkConstants.DEDICATED_STATUS_HUNTER;
        case CourseTypeConstants.DEDICATED_SPORTS_PERSON:
            return ExternalLinkConstants.DEDICATED_STATUS_SPORTPERSON;
        case CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON:
            return ExternalLinkConstants.DEDICATED_STATUS_HUNTER_AND_SPORTPERSON;
        default:
            return ExternalLinkConstants.DEDICATED_STATUS_HUNTER;
    }
};


